import { Upload, RefreshCw, Music2, Download, Loader2 } from "lucide-react";
import { useRef, useState } from "react";
import axios from "axios";
import API_BASE_URL from "../config";

export default function PracticeTrack() {

    const [file, setFile] = useState(null);

    const [loading, setLoading] = useState(false);

    const [progress, setProgress] = useState(0);

    const [status, setStatus] = useState("");

    const [downloadUrl, setDownloadUrl] = useState(null);

    const fileInputRef = useRef(null);

    const handleFileChange = (e) => {

        const selected = e.target.files?.[0];

        if (!selected) return;

        setFile(selected);

        setDownloadUrl(null);

        setLoading(false);

        setProgress(0);

        setStatus("");

    };

    const changeSong = () => {

        setDownloadUrl(null);

        setLoading(false);

        setProgress(0);

        setStatus("");

        fileInputRef.current?.click();

    };

    const generatePracticeTrack = async () => {

        if (!file) return;

        setLoading(true);

        setProgress(0);

        setStatus("Uploading song...");

        const timer = setInterval(() => {

            setProgress((old) => {

                if (old >= 95) return old;

                return old + 2;

            });

        }, 1500);

        try {

            const formData = new FormData();

            formData.append("file", file);

            setStatus("Separating vocals using AI...");

            const response = await axios.post(

                `${API_BASE_URL}/api/practice-track`,

                formData,

                {
                    responseType: "blob",
                }

            );

            clearInterval(timer);

            setProgress(100);

            setStatus("Practice Track Ready");

            const url = window.URL.createObjectURL(response.data);

            setDownloadUrl(url);

        }

        catch (err) {

            clearInterval(timer);

            console.error(err);

            alert("Failed to generate practice track.");

        }

        finally {

            setLoading(false);

        }

    };

    return (

        <div className="space-y-10">

            {/* Header */}

            <header>

                <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-3">

                    Tool · 06

                </div>

                <h1 className="font-sans font-extrabold text-5xl lg:text-6xl">

                    Practice Studio

                </h1>

                <p className="mt-3 text-muted-foreground max-w-3xl">

                    Remove vocals from any song and create a high-quality instrumental
                    practice version using AI. Perfect for flute practice and live
                    performance.

                </p>

            </header>

            {/* Upload Section */}

            {!file && (

                <div className="rounded-2xl border border-border bg-card p-12">

                    <label className="border-2 border-dashed border-border rounded-2xl p-20 flex flex-col items-center justify-center cursor-pointer hover:border-primary transition-all">

                        <Upload
                            size={60}
                            className="text-primary mb-6"
                        />

                        <h2 className="text-3xl font-bold">

                            Choose Audio File

                        </h2>

                        <p className="mt-3 text-muted-foreground">

                            MP3 • WAV • FLAC • M4A

                        </p>

				<input
					id="audioFile"
					type="file"
					className="hidden"
					onChange={(e) => {
						if (e.target.files?.length) {
							setFile(e.target.files[0]);
						}
					}}
				/>

                    </label>

                </div>

            )}

            {file && (

                <>

                    {/* Uploaded Song */}

                    <div className="rounded-2xl border border-border bg-card p-8">

                        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-8">

                            <div>

                                <div className="flex items-center gap-3 mb-3">

                                    <Music2 className="text-primary" />

                                    <h2 className="text-2xl font-bold">

                                        Uploaded Song

                                    </h2>

                                </div>

                                <div className="text-xl font-semibold">

                                    {file.name}

                                </div>

                                <div className="text-muted-foreground mt-2">

                                    {(file.size / 1024 / 1024).toFixed(2)} MB •{" "}
                                    {file.name.split(".").pop()?.toUpperCase()}

                                </div>

                            </div>

                            <button

                                onClick={changeSong}

                                className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-full border border-border hover:border-primary transition-all"

                            >

                                <RefreshCw size={18} />

                                Change Song

                            </button>

                        </div>

                    </div>

                    <input

                        ref={fileInputRef}

                        type="file"

                        accept="audio/*"

                        className="hidden"

                        onChange={handleFileChange}

                    />

                    {/* Practice Track */}

                    <div className="rounded-2xl border border-border bg-card p-8">

                        <div className="flex items-center gap-3 mb-4">

                            <div className="text-4xl">

                                🎤

                            </div>

                            <div>

                                <h2 className="text-3xl font-bold">

                                    Practice Track

                                </h2>

                                <p className="text-muted-foreground mt-1">

                                    Remove vocals and generate an instrumental version
                                    for flute practice.

                                </p>

                            </div>

                        </div>
						
						                        {!downloadUrl && (

                            <button

                                onClick={generatePracticeTrack}

                                disabled={loading}

                                className="mt-8 h-12 px-8 rounded-full bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-all disabled:opacity-60"

                            >

                                {loading ? (

                                    <span className="flex items-center gap-2">

                                        <Loader2
                                            size={18}
                                            className="animate-spin"
                                        />

                                        {status}

                                    </span>

                                ) : (

                                    "Generate Practice Track"

                                )}

                            </button>

                        )}

                        {loading && (

                            <div className="mt-8 rounded-xl border border-border bg-secondary/20 p-6">

                                <div className="flex justify-between mb-3">

                                    <span className="font-medium">

                                        {status}

                                    </span>

                                    <span>

                                        {progress}%

                                    </span>

                                </div>

                                <div className="w-full h-3 rounded-full bg-border overflow-hidden">

                                    <div

                                        className="h-3 bg-primary transition-all duration-700"

                                        style={{
                                            width: `${progress}%`,
                                        }}

                                    />

                                </div>

                                <p className="text-sm text-muted-foreground mt-4">

                                    AI vocal separation usually takes
                                    around 1–2 minutes depending on the
                                    song length.

                                </p>

                            </div>

                        )}

                        {downloadUrl && (

                            <div className="mt-10 rounded-2xl border border-green-600 bg-green-900/10 p-8">

                                <div className="flex items-center gap-3 mb-6">

                                    <div className="text-3xl">

                                        ✅

                                    </div>

                                    <div>

                                        <h3 className="text-2xl font-bold text-green-400">

                                            Practice Track Ready

                                        </h3>

                                        <p className="text-muted-foreground">

                                            Your karaoke version has been generated successfully.

                                        </p>

                                    </div>

                                </div>

                                <div className="rounded-xl border border-border bg-background/40 p-6">

                                    <div className="font-semibold mb-4">

                                        Preview

                                    </div>

                                    <audio

                                        controls

                                        src={downloadUrl}

                                        className="w-full"

                                    />

                                </div>

                                <div className="mt-8 flex justify-center">

                                    <a

                                        href={downloadUrl}

                                        download="practice-track.mp3"

                                        className="inline-flex items-center gap-3 px-8 h-12 rounded-full bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-all"

                                    >

                                        <Download size={20} />

                                        Download Practice Track

                                    </a>

                                </div>

                            </div>

                        )}

                    </div>

                </>

            )}

        </div>

    );

}